import '../../styles/styles.css';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Layout from '../../components/Layout';

import AnimateIn from '../../components/AnimateIn';
import VimeoVideo from '../../components/VimeoVideo';
import Image from '../../components/Image';
import ColumnLists from '../../components/ColumnLists';
import LinkButton from '../../components/LinkButton';
import LinkList from '../../components/LinkList';
import Quote from '../../components/Quote';
import WorkHeader from '../../components/WorkHeader';

import { getProject } from '../../config/general';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const LEELA = ({featuredProjectsList}) => {
  const project = getProject('the-fabricant');

  return (
    <Layout metaData={{ data: project }} featuredProjectsList={featuredProjectsList}>
      <article className="components page">
        <WorkHeader component={{ project }} />

        <VimeoVideo
          component={{
            posterVideo: '441018559',
           caption:'Welcome to LEELA, your metaverse playground for self-expression.'
          }}
        />

        <AnimateIn className="text g-row">
          <p>
           In a time when digital has taken on more forms and meaning in our lives, it has also become a necessity for the world of fashion.
          </p>
          <p>
            Together with The Fabricant, we researched and developed a breakthrough in digital fashion. Introducing LEELA—a revolutionary way for everyone to explore their self-expression beyond what is physically possible.
          </p>
        </AnimateIn>

        <Image
          className=""
          component={{
            image: '/fabricant/Leela-Case-Study-01.jpg',
            calculateRatio: '1920,4303',
            caption: 'Dress up your multiple selves in the non-physical world of digital fashion.'
          }}
        />
        
       <AnimateIn className="text g-row">
          <h2 id="">Challenging the norms of fashion and identity</h2>
          <p>
            As Gen Z gradually becomes the defining generation of modern culture, their new expectations on fashion are on the rise. Today’s fashion needs to provide people with new ways to express their ever-changing identities while remaining environmentally conscious. 
          </p>
          <p>
            Centering around this shifting paradigm, the LEELA experience was architected to be fluid in its interaction and expression.
          </p>
        </AnimateIn>

        <Image
          component={{
            image: '/fabricant/Leela-Case-Study-02.jpg',
            calculateRatio: '1920,4285'
          }}
        />
        
         <AnimateIn className="text g-row">
          <h2 id="">‘LEELA’ stands for dancing and playing</h2>
          <p>
            The word LEELA comes from the Hindi language, meaning to dance and play. LEELA’s mission is to provide a self-expressive playground, not merely a fashion e-commerce site. This ‘play’ ethos inspired many of our design decisions and lends itself throughout the interactive framework and visual language.
          </p>
        </AnimateIn>
    
        <Image
          component={{
            image: '/fabricant/Leela-Case-Study-03.jpg',
            calculateRatio: '1920,5557'
          }}
        />
  
        <Quote 
          className="g-row"
          component={{
            quote:
              '"Your Majesty understands the creative and technical aspects of visualizing and presenting fashion in engaging ways. They find new ways to deliver the desired results on time and at high-quality standards."',
              quotee: 'Adriana Hoppenbrouwer — Founder and Commercial Director, The Fabricant'
          }}
        />
    
        <AnimateIn className="text g-row">
          <h2 id="">Harnessing ambiguity to fuel innovation</h2>
          <p>
            As with any research and development initiative, we came into this project with unknowns, from both a technical and market-fit perspective. 
          </p>
          <p>
            To tackle these unknowns, we opted for an AGILE and embedded collaboration with our clients. Along with several guerilla tests throughout the project, this process allowed us to get quick feedback, fail fast, and innovate even faster.
          </p>
        </AnimateIn>
    
        <Image
          component={{
            image: '/fabricant/Leela-Case-Study-04.jpg',
            calculateRatio: '1920,1930',
            caption: 'The LEELA Beta was launched in April 2020 and was met with a significant engagement and positive recognitions from the fashion industry and world-leading publications.'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="outcomes">Outcomes</h2>
          <ul>
            <li>From idea to the first MVP in six weeks</li>
            <li>600+ digital avatars created within the first three days</li>
            <li>1800+ digital looks created and shared on social media within three days of launch</li>
            <li>Average session duration at 3:30 minutes</li>
            <li>27% bounce rate</li>
            <li>World-wide mediacoverage</li>
            <li>Validated market demand for digital self-expression</li>
            <li>Awarded{' '}<a href="https://www.cssdesignawards.com/sites/leela-beta/37245/" target="_blank">CSS Design Awards</a> for UI Design, UX Design and Innovation</li>
            <li>Awarded{' '}<a href="https://thefwa.com/cases/leela-beta" target="_blank">FWA Of The Day</a></li>
            <li>Awarded{' '}<a href="https://www.awwwards.com/sites/leela-beta/mobile-excellence-report" target="_blank">Awwwards Mobile Excellence</a></li>
           </ul>
        
          <h2 id="Press">Press</h2>
          <ul>
            <li>The Guardian:{' '}<a href="https://www.theguardian.com/fashion/2020/apr/10/fashion-meets-fortnite-rise-of-3d-clothes-digital-catwalks" target="_blank">Fashion meets Fortnite: 3D clothes and digital catwalks</a></li>
            <li>Vogue Business:{' '}<a href="https://www.voguebusiness.com/technology/digital-fashion-surges-in-a-sales-downturn-forma-drest" target="_blank">Digital fashion surges in a sales downturn</a></li>
            <li>Forbes:{' '}<a href="https://www.forbes.com/sites/brookerobertsislam/2020/04/06/virtual-catwalks-and-digital-fashion-how-covid-19-is-changing-the-fashion-industry/" target="_blank">Virtual Catwalks And Digital Fashion</a></li>
            <li>Mission:{' '}<a href="https://missionmag.org/is-digital-clothing-the-future-of-fashion/" target="_blank">The Fabricant wastes nothing but data and exploits nothing but imagination</a></li>
            <li>Trendland:{' '}<a href="https://trendland.com/your-digital-avatar-with-leela/" target="_blank">Your digital avatar with LEELA</a></li>
            <li>LS:N:{' '}<a href="https://www.lsnglobal.com/fashion/article/25476/the-fabricant-gives-access-to-digital-couture" target="_blank">The Fabricant gives access to digital couture</a></li>
            <li>HYPEBEAST:{' '}<a href="https://hypebeast.cn/2020/7/how-do-fashion-brands-present-brand-value-through-web-design-adidas-cdg-caseycasey-fila-twitter-gucci" target="_blank">How do fashion brands present value through web design</a></li>
          </ul>    
       </AnimateIn>
    
        <AnimateIn className="text g-row">
          <p> 
            <a href="http://digital.fashion" target="_blank">Experience LEELA</a>
            <br/><br/>
            Learn more about the <a href="http://www.thefabricant.com" target="_blank">The Fabricant</a>.
          </p>
          <h2 id="look-to-innovate">Are you looking to innovate?</h2>
          <p>
            Contact{' '}
            <a href="mailto: hello@yourmajesty.co">
              hello@yourmajesty.co
            </a>{' '}
            and find out how Your Majesty help you launch your next digital product.
          </p>
          
          <h2 id="">For more fashion and apparel</h2>
          <p>
            Read our opinion piece,{' '}<a href="https://medium.com/your-majesty-co/digital-fashion-a-new-essential-7ee93e7c0eb7" target="_blank">Digital Fashion: A New Essential</a>  
    {' '}and check out our case studies for <a href="/work/fashion-for-good">Fashion For Good</a>, <a href="/work/i-t-hong-kong">I.T Hong Kong</a> and <a href="/work/fila">FILA Explore</a>.
          </p>
        </AnimateIn>

        <ColumnLists
          className="g-row"
          component={{
            title: 'Services',
            columns: [
              {
                title: 'Data & Advanced Analytics',
                items: [
                  'Data Analysis',
                  'Content & Analytics Audit',
                  'Expert Reviews',
                  'Site Performance Analysis',
                  'Custom Site Events',
                  'Quantitative User Research',
                  'Qualitative User Research',
                  'A/B Testing'
                  ]
              },
              {
                title: 'Insights & Strategy',
                items: [
                  'Insight Mining & Synthesis',
                  'Product Vision & Proposition',
                  'Business Model & Impact Forecast',
                  'Innovation Canvas',
                  'Roadmap & Go-To-Market',
                  'Brand Health Check',
                  'Industry Positioning'
                 ]
              },

              {
                title: 'Design',
                items: [
                  'UI/UX Design',
                  'Art Direction',
                  'Creative Direction',
                  'Visual Design',
                  'Motion Design',
                  'Brand Design',
                  '3D Design',
                  '3D Animation'
                ]
              },

              {
                title: 'Technology',
                items: [
                  'Front-end Development',
                  'Back-end Development',
                  'StoryBlok CMS',
                  'WebGL',
                  'Systems Architecture',
                  'Functional Prototyping'
                  ]
              },

              {
                title: 'Delivery & Aftercare',
                items: [
                  'Quality Assurance',
                  'Deployment',
                  'Integration Support',
                  'Staff Training',
                  'Monitoring',
                  'Perforamance Optimization',
                  'Operations & Maintenance'
                ]
              }
            ]
          }}
        />
      </article>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default LEELA;
